import React from "react";
import Events from "../../../../../utilities/EventEmitter";
import { MapConstant } from "../../../../../lib/Map";
import styles from "../../../../../layout/componentHeaderStyles";
import { withStyles } from "@material-ui/core";
import Tooltip from "../../../../common/StyledToolTip";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import drawConstant from "./DrawConstant";
class DrawingToolbox extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      draw: null,
      activeDrawMode: null,
      map: null,
      activeFeatureId: null,
      enableDrawing: true,
      selectedFeatureId: null,
    };
    this.mapId = props.useMapIdWithEvent && props.mapId ? `${props.mapId}_` : "";
    /* Pass mapId, useMapIdWithEvent as props to avoid overlapping events across multiple component using same events to emit or listen.*/
  }

  componentDidMount() {
    Events.on(`${this.mapId}${MapConstant.EVENT.SET_MAP_DRAW}`, this.updateDrawObject);
    Events.on(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP}`,
      this.handleCreatedDrawingOnMap
    );
    Events.on(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_SIDE_PANEL}`,
      this.drawingSelectedOnSidePanel
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.props.hide !== prevProps.hide) {
      this.setState({
        enableDrawing: true,
        activeFeatureId: null,
        activeDrawMode: null,
        selectedFeatureId: null
      });
      this.state.draw?.deleteAll();
    }
  }

  componentWillUnmount() {
    Events.off(`${this.mapId}${MapConstant.EVENT.SET_MAP_DRAW}`, this.updateDrawObject);
    Events.off(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP}`,
      this.handleCreatedDrawingOnMap
    );
    Events.off(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_SIDE_PANEL}`,
      this.drawingSelectedOnSidePanel
    );
    this.clearMapEvents();
  }

  addMapEvents = () => {
    const { map } = this.state;
    if (map) {
      map.on(MapConstant.DRAWING_EVENTS.MODE_CHANGE, this.onDrawModeChange)
      map.on(MapConstant.DRAWING_EVENTS.CREATE, this.drawUpdated);
      map.on(MapConstant.DRAWING_EVENTS.UPDATE, this.drawUpdated);
      map.on(MapConstant.DRAWING_EVENTS.DELETE, this.drawDeleted);
      map.on(
        MapConstant.DRAWING_EVENTS.SELECTION_CHANGE,
        this.drawSelectionChanged
      );
    }
  };

  drawingSelectedOnSidePanel = (featureIds, isEditMode = false) => {
    featureIds.length > 0 &&
      this.state.draw?.changeMode(this.state.draw?.modes?.SIMPLE_SELECT, {
        featureIds: featureIds,
      });
    if (isEditMode) {
      this.setState({
        activeFeatureId: featureIds[0],
        selectedFeatureId: featureIds[0],
        enableDrawing: false,
      });
    } else {
      this.setState({
        selectedFeatureId: featureIds[0],
      });
    }
    Events.emit(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP}`,
      featureIds
    );
  };

  clearMapEvents = () => {
    const { map, draw } = this.state;
    if (map || draw) {
      map.off(MapConstant.DRAWING_EVENTS.MODE_CHANGE, this.onDrawModeChange);
      map.off(MapConstant.DRAWING_EVENTS.CREATE, this.drawUpdated);
      map.off(MapConstant.DRAWING_EVENTS.UPDATE, this.drawUpdated);
      map.off(MapConstant.DRAWING_EVENTS.DELETE, this.drawDeleted);
      map.off(
        MapConstant.DRAWING_EVENTS.SELECTION_CHANGE,
        this.drawSelectionChanged
      );
    }
  };

  onDrawModeChange = () => {
    //will disable lasso when user click ESC button or double click on map
    this.changeDrawMode({mode: MapConstant.DISABLE_LASSO_MODE});
  }

  drawSelectionChanged = (event) => {
    if (Boolean(this.state.activeFeatureId)) {
      //check if there is an activeDrawing in progress, If true then prevent user from drawing or updating other drawings on map.
      if (
        event.features.length &&
        event.features[0].id !== this.state.activeFeatureId
      ) {
        this.preventDrawUpdate(event.features[0].id);
      }
      return;
    }
    Events.emit(
      `${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP}`,
      event.features.map((feature) => feature.id)
    );
    this.setState({ selectedFeatureId: event.features[0]?.id ?? null });
  };

  preventDrawUpdate = () => {
    Events.emit(`${this.mapId}${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_IN_PROGRESS}`);
    this.state.draw.changeMode(this.state.draw?.modes?.SIMPLE_SELECT, {
      featureIds: [this.state.activeFeatureId],
    });
  };

  updateDrawObject = ({ map, draw, drawComplete }) => {
    this.clearMapEvents();
    this.setState(
      {
        draw,
        map,
      },
      this.addMapEvents
    );
    this.drawComplete = drawComplete;
  };

  changeDrawMode = (drawMode) => {
    const { draw, activeFeatureId } = this.state;
    if (activeFeatureId) {
      return;
    }
    if (drawMode.mode === MapConstant.DISABLE_LASSO_MODE) {
      this.setState({
        activeDrawMode: null,
      });
      return;
    }
    // if feature is drawing is present always go direct select mode(allows to move vertices) from simple select
    const [mode, options] =
      draw.modes.SIMPLE_SELECT === drawMode.mode &&
      draw.getAll().features.length
        ? [
            draw.modes.DIRECT_SELECT,
            { featureId: draw.getAll().features[0].id },
          ]
        : [drawMode.mode, {}];
    this.setState({
      activeDrawMode: mode,
    });
    draw.changeMode(mode, options);
  };

  clearDraw = () => {
    if (this.state.draw && this.state.selectedFeatureId) {
      const selectedIds = this.state.draw.getSelectedIds();
      if (selectedIds.length) {
        Object.values(this.state.draw.getAll().features).forEach((feature) => {
          if (selectedIds.includes(feature.id)) {
            this.drawDeleted(feature);
          }
        });
      }
    }
  };

  handleCreatedDrawingOnMap = (action) => {
    if (action === "save") {
      this.setState({
        activeFeatureId: null,
        enableDrawing: true,
      });
      return;
    }
    this.setState({
      activeFeatureId: null,
      enableDrawing: true,
      selectedFeatureId: null,
    });
    this.state.draw?.changeMode?.(this.state.draw?.modes?.SIMPLE_SELECT, {});
  };

  drawUpdated = (event) => {
    this.drawComplete(event.features[0], event.type);
    this.setState({
      activeFeatureId: event.features[0].id,
      activeDrawMode: null,
      enableDrawing: false,
      selectedFeatureId: null,
    });
  };

  drawDeleted = (feature) => {
    if (this.state.activeFeatureId === feature.id) {
      this.setState({
        activeFeatureId: null,
        enableDrawing: true,
      });
    }
    this.drawComplete(feature, "delete");
  };

  getLassoTitle = (disableDraw, enableDrawing) => {
    if (disableDraw) {
      return 'Disabled';
    } else if (enableDrawing) {
      return 'Draw a shape';
    }
    return 'Save or delete drawn polygon to draw a new one.';
  };

  getClearTitle = (disableDraw, selectedFeatureId) => {
    if (disableDraw) {
      return 'Disabled';
    } else if (selectedFeatureId) {
      return 'Delete selected polygon';
    }
    return 'Select a polygon to delete';
  };

  render() {
    const { classes, disableDraw, hide } = this.props;
    const { draw, activeDrawMode, enableDrawing, selectedFeatureId } = this.state;
    if (hide) {
      return null;
    }
    return (
      <React.Fragment>
        {this.props.lasso && (
          <React.Fragment>
            <div
              className={`${classes.headerIconStyles} ${classes.iconLayout} glHeaderIcons ${
                draw && enableDrawing && !disableDraw ? classes.iconStyle : classes.disableIcon
              } ${
                draw && activeDrawMode === draw.modes.DRAW_POLYGON && !disableDraw && !this.props.singleColorDraw
                  ? classes.activeIcon
                  : ''
              }`}
              onClick={
                !disableDraw
                  ? () =>
                      this.changeDrawMode({
                        mode: draw ? draw.modes.DRAW_POLYGON : null
                      })
                  : null
              }>
              <Tooltip title={this.getLassoTitle(disableDraw, enableDrawing)}>
                <i className={`icon-lasso-select ${classes.actionIcon}`} />
              </Tooltip>
            </div>
          </React.Fragment>
        )}

        {this.props.delete && (
          <div
            className={`${classes.headerIconStyles} ${classes.iconLayout} glHeaderIcons ${
              selectedFeatureId && !disableDraw ? classes.enabledIcon : classes.disableIcon
            }`}
            onClick={this.clearDraw}>
            <Tooltip title={this.getClearTitle(disableDraw, selectedFeatureId)}>
              <i className={`icon-icon_delete ${classes.actionIcon}`} />
            </Tooltip>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default compose(withSnackbar, withStyles(styles))(DrawingToolbox);
